/** Client-side filter + export helpers for salary assignments grid. */

export function computeGrossBounds(rows) {
  const values = (rows || [])
    .map((r) => {
      const n = Number(r.grossSalary);
      return Number.isFinite(n) && n >= 0 ? n : null;
    })
    .filter((n) => n != null);
  if (values.length === 0) return { min: 0, max: 100000, hasData: false };
  const min = Math.floor(Math.min(...values));
  const max = Math.ceil(Math.max(...values));
  return { min, max: max > min ? max : min + 1000, hasData: true };
}

export function createFullRangeGrossFilter(bounds) {
  return { min: bounds.min, max: bounds.max };
}

export function isFullRangeGrossFilter(grossFilter, bounds) {
  if (!grossFilter || !bounds) return true;
  const min = Number(grossFilter.min);
  const max = Number(grossFilter.max);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return true;
  return min <= bounds.min && max >= bounds.max;
}

export function rowMatchesGrossFilter(row, grossFilter, bounds) {
  if (!grossFilter || isFullRangeGrossFilter(grossFilter, bounds)) return true;

  const gross = row.grossSalary;
  const hasGross = gross != null && gross !== "" && Number.isFinite(Number(gross));
  if (!hasGross) return false;

  const n = Number(gross);
  const min = Number(grossFilter.min);
  const max = Number(grossFilter.max);
  if (Number.isFinite(min) && n < min) return false;
  if (Number.isFinite(max) && n > max) return false;
  return true;
}

export function filterSalaryAssignmentRows(rows, filters) {
  const {
    employeeSearch = "",
    departmentId = "all",
    officeId = "all",
    projectId = "all",
    payrollStructureId = "all",
    grossFilter = null,
    grossBounds = { min: 0, max: Infinity },
  } = filters;

  const q = String(employeeSearch || "").trim().toLowerCase();

  return (rows || []).filter((row) => {
    if (q) {
      const name = String(row.employeeName || "").toLowerCase();
      if (!name.includes(q)) return false;
    }
    if (departmentId !== "all" && String(row.departmentId ?? "") !== String(departmentId)) return false;
    if (officeId !== "all" && String(row.officeId ?? "") !== String(officeId)) return false;
    if (projectId !== "all") {
      const pids = Array.isArray(row.projectIds) ? row.projectIds : [];
      if (!pids.some((id) => String(id) === String(projectId))) return false;
    }
    if (payrollStructureId !== "all") {
      const sid = String(row.payrollStructureId || "");
      if (payrollStructureId === "none") {
        if (sid) return false;
      } else if (sid !== String(payrollStructureId)) {
        return false;
      }
    }
    if (!rowMatchesGrossFilter(row, grossFilter, grossBounds)) return false;
    return true;
  });
}

function escapeCsvCell(value) {
  const s = value == null ? "" : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function rowsToExportMatrix(rows, t) {
  const headers = [
    t("app.payroll.assignments.colEmployee"),
    t("app.payroll.assignments.colDepartment"),
    t("app.payroll.assignments.colOffice"),
    t("app.payroll.assignments.colStructure"),
    t("app.payroll.assignments.colGross"),
  ];
  const data = (rows || []).map((r) => [
    r.employeeName || "",
    r.departmentName || "",
    r.workplaceName || "",
    r.payrollStructureName || r.payrollStructureId || "",
    r.grossSalary != null && r.grossSalary !== ""
      ? `${r.grossSalary}${r.currencyCode ? ` ${r.currencyCode}` : ""}`
      : "",
  ]);
  return [headers, ...data];
}

export function downloadSalaryAssignmentsCsv(rows, t, filenameBase = "salary-assignments") {
  const matrix = rowsToExportMatrix(rows, t);
  const lines = matrix.map((row) => row.map(escapeCsvCell).join(","));
  const bom = "\uFEFF";
  const blob = new Blob([bom + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  triggerDownload(blob, `${filenameBase}.csv`);
}

export function downloadSalaryAssignmentsExcel(rows, t, filenameBase = "salary-assignments") {
  const matrix = rowsToExportMatrix(rows, t);
  const lines = matrix.map((row) => row.map(escapeCsvCell).join("\t"));
  const blob = new Blob([lines.join("\r\n")], {
    type: "application/vnd.ms-excel;charset=utf-8",
  });
  triggerDownload(blob, `${filenameBase}.xls`);
}

export function downloadSalaryAssignmentsPdf(rows, t, filenameBase = "salary-assignments") {
  const matrix = rowsToExportMatrix(rows, t);
  const title = t("app.payroll.assignments.title");
  const headHtml = matrix[0].map((h) => `<th>${escapeHtml(h)}</th>`).join("");
  const bodyHtml = matrix
    .slice(1)
    .map((row) => `<tr>${row.map((c) => `<td>${escapeHtml(c)}</td>`).join("")}</tr>`)
    .join("");
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${escapeHtml(title)}</title>
<style>
body{font-family:var(--app-font-family,"Inter",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif);padding:24px;color:#0f172a}
h1{font-size:1.25rem;margin:0 0 16px}
table{border-collapse:collapse;width:100%;font-size:12px}
th,td{border:1px solid #cbd5e1;padding:8px 10px;text-align:left}
th{background:#f1f5f9;font-weight:700;text-transform:uppercase;font-size:10px;letter-spacing:.06em}
tr:nth-child(even) td{background:#fafbfc}
</style></head><body>
<h1>${escapeHtml(title)}</h1>
<table><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>
</body></html>`;
  const win = window.open("", "_blank", "noopener,noreferrer");
  if (!win) return false;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
  return true;
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function triggerDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
